import {
  CalendarDays,
  Check,
  ChevronLeft,
  Clock3,
  LayoutGrid,
  Palette,
  QrCode,
  ScanLine,
  Search,
  ShoppingBag,
  Users,
  Utensils,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const menuItems = [
  ["چلو کباب مخصوص", "۴۸۵٬۰۰۰ تومان", "/images/story/kebab.jpg"],
  ["ماهی فصل", "۳۹۰٬۰۰۰ تومان", "/images/story/fish.jpg"],
  ["سالاد نارنج", "۱۸۵٬۰۰۰ تومان", "/images/story/salad.jpg"],
];

const MenuPreview = ({ compact = false }) => (
  <div
    data-menu-preview
    className="overflow-hidden rounded-[2rem] bg-white text-right shadow-[0_24px_60px_rgba(16,43,35,.16)]"
  >
    <div className="relative h-28 overflow-hidden bg-[#171411]">
      <img
        src="/images/story/menu-header.jpg"
        alt=""
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
      <div className="absolute inset-x-4 bottom-3 text-white">
        <p className="font-black">رستوران نارنج</p>
        <p className="text-[10px] text-white/70">طعم ایرانی، میزبانی مدرن</p>
      </div>
    </div>
    <div className="p-3">
      <div className="flex items-center gap-2 rounded-full bg-[#f3f7f5] px-3 py-2 text-[10px] text-gray-400">
        <Search size={12} />
        جست‌وجوی غذا
      </div>
      <div data-menu-categories className="mt-3 flex gap-2">
        {["همه", "غذای اصلی", "نوشیدنی"].map((label, index) => (
          <span
            key={label}
            className={`rounded-full px-2.5 py-1 text-[9px] font-bold ${
              index
                ? "bg-[#eaf5f2] text-[#153a2e]"
                : "bg-primary text-white"
            }`}
          >
            {label}
          </span>
        ))}
      </div>
      <div className="mt-3 space-y-2">
        {menuItems.slice(0, compact ? 2 : 3).map(([name, price, image]) => (
          <article
            data-menu-row
            key={name}
            className="flex items-center gap-2 rounded-xl border border-[#e3eeea] bg-white p-2"
          >
            <img src={image} alt="" className="h-11 w-11 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[10px] font-extrabold text-[#153a2e]">
                {name}
              </p>
              <p className="text-[9px] font-bold text-primary">{price}</p>
            </div>
            <span className="grid h-7 w-7 place-items-center rounded-full bg-primary text-xs font-bold text-white">
              +
            </span>
          </article>
        ))}
      </div>
    </div>
  </div>
);

export const HeroMenuPreview = () => (
  <div className="w-[19rem]" aria-hidden="true">
    <MenuPreview compact />
  </div>
);

export const MenuSurface = () => (
  <div
    data-story-menu
    aria-hidden="true"
    className="absolute w-[20rem] max-w-[76vw] will-change-transform"
  >
    <MenuPreview />
  </div>
);

export const CustomizationSurface = () => (
  <div
    data-story-customize
    aria-hidden="true"
    className="absolute w-[18rem] max-w-[72vw] rounded-3xl bg-white p-4 text-right text-[#153a2e] shadow-2xl will-change-transform"
  >
    <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
      <Palette size={16} className="text-primary" />
      <strong className="text-sm">ظاهر منو</strong>
    </div>
    <p className="mt-4 text-[10px] font-bold text-gray-500">رنگ اصلی</p>
    <div className="mt-2 flex gap-2">
      {["#153a2e", "#f88225", "#c79a4b", "#7c3aed"].map((color) => (
        <span
          data-custom-swatch={color === "#f88225" ? "active" : ""}
          key={color}
          className="h-7 w-7 rounded-full border-2 border-white shadow ring-1 ring-gray-200"
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
    <p className="mt-4 text-[10px] font-bold text-gray-500">قالب فعال</p>
    <div className="mt-2 rounded-xl border-2 border-primary bg-primary/5 p-3">
      <strong className="text-xs">قالب امضای میزبان</strong>
      <p className="mt-1 text-[9px] text-gray-500">
        سبز تیره · طلایی · زمینه کرم
      </p>
    </div>
    <p className="mt-4 text-[10px] font-bold text-gray-500">دسته‌بندی‌ها</p>
    <div className="mt-2 flex flex-wrap gap-1.5">
      {["غذای اصلی", "پیش‌غذا", "نوشیدنی"].map((label) => (
        <span
          key={label}
          className="rounded-full bg-gray-100 px-2 py-1 text-[9px] font-bold"
        >
          {label}
        </span>
      ))}
    </div>
    <div className="mt-4 flex items-center gap-3 rounded-xl bg-[#f8f5ec] p-3">
      <span className="grid h-10 w-10 place-items-center rounded-full bg-[#153a2e] text-sm font-black text-[#c79a4b]">
        ن
      </span>
      <div>
        <strong className="text-xs">نشان نارنج</strong>
        <p className="text-[9px] text-gray-500">هویت رستوران</p>
      </div>
    </div>
  </div>
);

export const QrSurface = () => (
  <div
    data-story-qr
    aria-hidden="true"
    className="absolute w-[34rem] max-w-[86%] will-change-transform"
  >
    <div className="relative overflow-hidden rounded-[2rem] shadow-[0_24px_60px_rgba(16,43,35,.2)]">
      <img
        src="/images/story/qr-table.jpg"
        alt=""
        className="aspect-[16/9] w-full object-cover"
      />
      <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[10px] font-bold text-white">
        <ScanLine size={12} />
        اسکن روی میز
      </span>
    </div>
    <div
      data-qr-card
      className="absolute -bottom-12 -right-2 w-40 rounded-3xl md:-right-14 bg-white p-4 text-center text-[#153a2e] shadow-2xl"
    >
      <div className="flex items-center justify-center gap-1.5">
        <QrCode size={14} className="text-primary" />
        <strong className="text-xs">QR میز ۱۲</strong>
      </div>
      <div className="relative mx-auto mt-3 w-fit overflow-hidden rounded-xl border-2 border-primary/30 p-2">
        <QRCodeSVG
          value="https://menu.bamizban.ir/narenj?table=12"
          size={96}
          fgColor="#153a2e"
          bgColor="#ffffff"
          level="M"
        />
        <span
          data-qr-scan
          className="absolute inset-x-1 h-0.5 rounded-full bg-primary"
          style={{ top: "6%" }}
        />
      </div>
      <p className="mt-2 truncate text-[8px] text-gray-500" dir="ltr">
        menu.bamizban.ir/narenj
      </p>
    </div>
  </div>
);

const IDLE_TABLE = { borderColor: "#e5e7eb" };
const SELECTED_TABLE = {
  backgroundColor: "#f88225",
  borderColor: "#f88225",
  color: "#fff",
};

export const ReservationSurface = () => (
  <div
    data-story-reservation
    aria-hidden="true"
    className="absolute w-[36rem] max-w-[92%] will-change-transform"
  >
    <img
      src="/images/story/reservation-room.jpg"
      alt=""
      className="mr-auto block aspect-[16/10] w-[78%] rounded-[2rem] object-cover shadow-[0_24px_60px_rgba(16,43,35,.2)]"
    />
    <div className="absolute right-0 top-1/2 w-[21rem] max-w-[80%] -translate-y-1/2 md:max-w-[62%] rounded-[1.75rem] bg-white p-5 text-right text-gray-900 shadow-2xl">
      <div className="flex items-center gap-2">
        <CalendarDays className="text-primary" size={18} />
        <strong>رزرو میز</strong>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          ["تاریخ", "پنجشنبه ۳ مهر"],
          ["ساعت", "۲۰:۳۰"],
          ["مهمان", "۴ مهمان"],
        ].map(([label, value]) => (
          <div
            data-reservation-field
            key={label}
            className="rounded-xl border border-gray-200 p-2"
          >
            <p className="text-[9px] text-gray-400">{label}</p>
            <strong className="mt-1 block text-[10px]">{value}</strong>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[10px] font-bold text-gray-500">انتخاب میز</p>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {["میز ۸", "میز ۱۰", "میز ۱۲", "میز ۱۴"].map((label, index) => (
          <span
            data-reservation-table={index === 2 ? "selected" : undefined}
            key={label}
            // Colours are inline, not Tailwind classes (which are !important
            // here), so the timeline can start table 12 neutral and select it.
            className="rounded-xl border px-2 py-2 text-center text-[9px] font-bold"
            style={index === 2 ? SELECTED_TABLE : IDLE_TABLE}
          >
            {label}
          </span>
        ))}
      </div>
      <div
        data-reservation-confirm
        className="mt-4 flex items-center gap-2 rounded-xl bg-[#eaf5f2] p-3 text-[#1f6d5c]"
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-[#1f6d5c] text-white">
          <Check size={14} />
        </span>
        <div>
          <strong className="text-[10px]">رزرو شما تأیید شد</strong>
          <p className="text-[9px]">پنجشنبه، ساعت ۲۰:۳۰ · میز ۱۲</p>
        </div>
      </div>
    </div>
  </div>
);

const dashboardKpis = [
  [CalendarDays, "رزروهای امروز", "۱۲"],
  [Users, "مهمان‌ها", "۳۸"],
  [ShoppingBag, "سفارش‌های جدید", "۷"],
];

export const DashboardSurface = () => (
  <div
    data-story-dashboard
    aria-hidden="true"
    className="absolute w-[94%] max-w-[48rem] rounded-3xl border border-gray-200 bg-[#f8faf9] p-5 text-right text-gray-900 shadow-2xl will-change-transform"
  >
    <div className="flex items-center justify-between">
      <div>
        <strong className="text-base text-[#153a2e]">نمای امروز</strong>
        <p className="text-[10px] text-gray-500">
          اطلاعات عملیاتی رستوران در یک نگاه
        </p>
      </div>
      <span className="rounded-xl bg-white p-2 text-gray-400">
        <Clock3 size={16} />
      </span>
    </div>
    <div className="mt-4 grid grid-cols-3 gap-3">
      {dashboardKpis.map(([Icon, label, value]) => (
        <article
          data-dashboard-kpi
          key={label}
          className="rounded-2xl border border-gray-200 bg-white p-3"
        >
          <Icon size={16} className="text-primary" />
          <p className="mt-3 text-[9px] text-gray-500">{label}</p>
          <strong className="text-xl text-[#153a2e]">{value}</strong>
        </article>
      ))}
    </div>
    <div className="mt-3 grid grid-cols-[1.25fr_.75fr] gap-3">
      <section
        data-dashboard-panel
        className="rounded-2xl border border-gray-200 bg-white p-4"
      >
        <div className="flex items-center justify-between">
          <strong className="text-xs">وضعیت منو</strong>
          <span className="rounded-full bg-[#eaf5f2] px-2 py-1 text-[9px] font-bold text-[#1f6d5c]">
            منتشر شده
          </span>
        </div>
        <div className="mt-3 flex items-center gap-3 rounded-xl bg-[#f8f5ec] p-3">
          <Utensils size={18} className="text-primary" />
          <div>
            <p className="text-[10px] font-bold">منوی اصلی رستوران</p>
            <p className="text-[9px] text-gray-500">۶ دسته‌بندی · ۴۸ آیتم</p>
          </div>
        </div>
      </section>
      <section
        data-dashboard-panel
        className="rounded-2xl border border-gray-200 bg-white p-4"
      >
        <strong className="text-xs">رزرو بعدی</strong>
        <p className="mt-3 text-lg font-black text-[#153a2e]">۲۰:۳۰</p>
        <p className="text-[9px] text-gray-500">
          مریم احمدی · ۴ نفر · میز ۱۲
        </p>
      </section>
    </div>
    <div
      data-dashboard-panel
      className="mt-3 flex gap-2 rounded-2xl border border-gray-200 bg-white p-3"
    >
      {[
        [LayoutGrid, "مدیریت میزها"],
        [Utensils, "ویرایش منو"],
        [ChevronLeft, "همه رزروها"],
      ].map(([Icon, label]) => (
        <span
          key={label}
          className="flex items-center gap-1.5 rounded-xl bg-gray-50 px-3 py-2 text-[9px] font-bold"
        >
          <Icon size={13} />
          {label}
        </span>
      ))}
    </div>
  </div>
);
